import type { ChatRequest, ChatResponse, ConfirmRequest, LanguageCode, ToolInput, ToolName, ToolOutput, Principal } from '@ampshift/shared';

export const API_BASE_URL: string = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string, readonly details: unknown = null) {
    super(message);
    this.name = 'ApiError';
  }
}

/** One session id per browser tab: patient context and pending actions are keyed on it server-side. */
export function getSessionId(): string {
  const key = 'ampshift.portal.sessionId';
  try {
    const existing = sessionStorage.getItem(key);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

export type AuthHeaders = { kind: 'cognito'; idToken: string } | { kind: 'dev'; principal: Principal };

export interface ApiClient {
  chat(req: ChatRequest): Promise<ChatResponse>;
  confirm(req: ConfirmRequest): Promise<ChatResponse>;
  tool<N extends ToolName>(name: N, input: ToolInput<N>): Promise<ToolOutput<N>>;
}

export interface ApiOptions {
  baseUrl?: string;
  auth: () => AuthHeaders | null;
  locale: () => LanguageCode;
  fetchImpl?: typeof fetch;
}

export function createApi(opts: ApiOptions): ApiClient {
  const baseUrl = opts.baseUrl ?? API_BASE_URL;
  const fetchImpl = opts.fetchImpl ?? ((input, init) => fetch(input, init));

  async function post<T>(path: string, body: unknown): Promise<T> {
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      'x-session-id': getSessionId(),
      'accept-language': opts.locale(),
    };
    const auth = opts.auth();
    if (auth?.kind === 'cognito') headers['authorization'] = `Bearer ${auth.idToken}`;
    if (auth?.kind === 'dev') headers['x-dev-principal'] = JSON.stringify(auth.principal);
    const res = await fetchImpl(`${baseUrl}${path}`, { method: 'POST', headers, body: JSON.stringify(body) });
    const text = await res.text();
    let json: unknown = null;
    try { json = text ? JSON.parse(text) : null; } catch { json = null; }
    if (!res.ok) {
      const err = (json as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
      throw new ApiError(res.status, err?.code ?? `HTTP_${res.status}`, err?.message ?? res.statusText, err?.details ?? null);
    }
    return json as T;
  }

  return {
    chat: (req) => post<ChatResponse>('/v1/chat', req),
    confirm: (req) => post<ChatResponse>('/v1/chat/confirm', req),
    tool: <N extends ToolName>(name: N, input: ToolInput<N>) => post<ToolOutput<N>>(`/v1/tools/${name}`, input),
  };
}
